import { TFunction } from 'i18next';
import { AIActionBinding, createAIActionBinding } from 'models/copilot/actions/aiActionBinding';
import {
  auditAssignProceduresTemplate,
  auditDivideWorkTemplate,
  auditStatusTemplate,
  processStartTemplate,
} from 'models/copilot/actions/aiActionTemplates';
import { CopilotAction } from 'models/copilot/copilotAction';
import { CopilotAuditPreparationContent } from 'models/copilot/copilotAuditPreparation';
import { CopilotPanelContentBase, CopilotPanelContext } from 'models/copilot/copilotBridge';
import {
  CopilotPanelEntityRef,
  CopilotPanelProcessState,
  CopilotPanelProgressRef,
  CopilotPanelRuntime,
} from 'models/copilot/copilotPanelContextDefinition';
import { CopilotPreviewContent } from 'models/copilot/copilotPreviewContent';
import { TenantProcessStateAuditFindings } from 'models/copilot/Processes/AuditFindings/tenantProcessStateAuditFindings';
import { TenantProcess, TenantProcessStatus } from 'models/copilot/tenantProcessStateBase';
import { EntityTypes } from 'models/entity';
import { AuditStatus } from 'models/internalAudit';
import { apiCopilotExecuteProcessAuditFindings } from 'services/Api/copilotService';
import { ProgressHubProcess } from 'services/SignalR/ProgressHub';
import { getAuditExecuteUrl, getAuditReportingUrl } from 'utils/url';
import { CopilotGenericPanelContextDefinition } from '../copilotGenericPanelContextDefinition';
import { getCopilotProcessWelcomeMessage } from '../copilotProcessMessages';
import { CopilotAuditFindingsPanelState } from './copilotAuditFindingsPanelState';

// The audit scene usage: plain chat with the audit as its subject and a set of ready-made questions,
// plus one process. The chat answer path is the generic one; the backend branches on this usage's
// context key and lets the audit agent handle it. The findings analysis is a process instead of a
// chat turn, because it writes tasks and so needs the user to approve a preview first.
export class CopilotAuditPreparationPanelContextDefinition extends CopilotGenericPanelContextDefinition {
  constructor() {
    super();
    this.context = CopilotPanelContext.AuditPreparation;
    this.name = 'AuditPreparation';
    this.supportsChat = true;
  }

  /**
   * The three questions plan the work, so they link to the execute tab: the list they change. The
   * findings analysis writes findings, which live on the reporting tab, so it links to that sub-tab.
   * Without an audit id the bindings still work from the panel; they carry no route to navigate to.
   */
  getBindings(content: CopilotPanelContentBase | undefined): AIActionBinding[] {
    const auditContent = content instanceof CopilotAuditPreparationContent ? content : undefined;
    const internalAuditId = auditContent?.internalAuditId || undefined;
    const executeRoute = internalAuditId ? getAuditExecuteUrl(internalAuditId) : undefined;
    const bindings: AIActionBinding[] = [
      createAIActionBinding(auditDivideWorkTemplate, undefined, { route: executeRoute }),
      createAIActionBinding(auditAssignProceduresTemplate, undefined, { route: executeRoute }),
      createAIActionBinding(auditStatusTemplate, undefined, { route: executeRoute }),
    ];

    // there are no results to analyse before the audit is executed; afterwards the analysis stays
    // available, because findings can still be managed once the audit is published
    const auditStatus = auditContent ? (auditContent.auditStatus ?? AuditStatus.Created) : undefined;
    if (auditStatus !== undefined && auditStatus >= AuditStatus.Executing) {
      bindings.push(
        createAIActionBinding(
          processStartTemplate,
          { processId: TenantProcess.AuditFindings },
          { route: internalAuditId ? getAuditReportingUrl(internalAuditId, 'findings') : undefined },
        ),
      );
    }

    return bindings;
  }

  async getAnswer(runtime: CopilotPanelRuntime, t: TFunction<string[]>): Promise<void> {
    const { action } = runtime;
    if (action?.processId !== TenantProcess.AuditFindings) {
      return super.getAnswer(runtime, t);
    }

    const state = this.getState(runtime);
    const internalAuditId = this.getInternalAuditId(runtime);
    if (!state || !internalAuditId) return;

    runtime.setIsLoading(true);
    const answer = await apiCopilotExecuteProcessAuditFindings(
      action.status || TenantProcessStatus.NotStarted,
      runtime.conversation,
      internalAuditId,
      state.newTasks,
      state.updatedTasks,
      state.originals,
      runtime.connectionId,
      runtime.accessToken,
      runtime.appContext.globalDataCache,
    );
    if (!answer) return;

    runtime.setConversation((prev) => runtime.mergeAdaptiveInputs(prev, answer.conversation));

    if (action.status === TenantProcessStatus.Completed) {
      //the server created and updated the findings; the audit scene reloads its findings list
      runtime.resultBus.publishAuditFindings(internalAuditId, answer.newTasks, answer.updatedTasks);

      return;
    }

    this.applyProposal(state, answer);
    if (answer.newTasks.length > 0 || answer.updatedTasks.length > 0) {
      runtime.setPreviewContent(CopilotPreviewContent.Usage);
    }

    //the analysis completes on its own when the findings are already complete: there is nothing to
    //review and nothing to approve, so the action must stop presenting itself as running
    if (answer.status === TenantProcessStatus.Completed) {
      await runtime.endProcess(TenantProcess.AuditFindings);
    }
  }

  getEntityRef(content: CopilotPanelContentBase | undefined): CopilotPanelEntityRef | undefined {
    if (!(content instanceof CopilotAuditPreparationContent) || !content.internalAuditId) return undefined;

    return { entityType: EntityTypes.InternalAudit, entityId: content.internalAuditId };
  }

  /**
   * The findings analysis is one model call per result plus one for the rewrites, so it runs for
   * minutes: the panel shows the resumable progress bar for as long as the action is running, which
   * covers both the analysis and every refinement round. The commit runs with the action already
   * marked Completed by the panel and is database work only, so it has no bar.
   */
  getProgressRef(
    runtime: Pick<CopilotPanelRuntime, 'action' | 'actions' | 'content'>,
  ): CopilotPanelProgressRef | undefined {
    const { action } = runtime;
    if (action?.processId !== TenantProcess.AuditFindings || action.status !== TenantProcessStatus.InProgress) {
      return undefined;
    }

    const entityRef = this.getEntityRef(runtime.content);
    if (!entityRef) return undefined;

    return {
      ...entityRef,
      tenantProcess: TenantProcess.AuditFindings,
      hubProcess: ProgressHubProcess.AuditFindings,
      completesInRequest: true,
    };
  }

  getWelcomeMessage(action: CopilotAction, t: TFunction<string[]>): string | undefined {
    if (action.processId !== TenantProcess.AuditFindings) return undefined;

    return getCopilotProcessWelcomeMessage(action, t);
  }

  getCreatePreviewText(t: TFunction<string[]>): string | undefined {
    return t('copilot:Actions.AuditFindings.Create');
  }

  // A new finding is created from a template, so it cannot be approved before the user picked one.
  // Without any eligible template in the tenant there is nothing to pick and the finding is created
  // plain, which the preview says.
  canSaveDisplayState(runtime: CopilotPanelRuntime): boolean {
    const state = this.getState(runtime);
    if (!state || state.eligibleTemplateIds.length === 0) return true;

    return state.newTasksDisplay.every((task) => task.templateId !== undefined && task.templateId !== null);
  }

  applyProcessState(runtime: CopilotPanelRuntime, state: CopilotPanelProcessState): CopilotPreviewContent | undefined {
    const panelState = this.getState(runtime);
    if (!(state.data instanceof TenantProcessStateAuditFindings) || !panelState) {
      return super.applyProcessState(runtime, state);
    }

    this.applyProposal(panelState, state.data);
    runtime.setConversation(state.conversation);

    return state.data.newTasks.length > 0 || state.data.updatedTasks.length > 0
      ? CopilotPreviewContent.Usage
      : undefined;
  }

  clearPreview(runtime: CopilotPanelRuntime): void {
    const state = this.getState(runtime);
    state?.setNewTasks([]);
    state?.setNewTasksDisplay([]);
    state?.setUpdatedTasks([]);
    state?.setUpdatedTasksDisplay([]);
    state?.setOriginals([]);
  }

  saveDisplayState(runtime: CopilotPanelRuntime): void {
    const state = this.getState(runtime);
    if (!state) return;

    state.setNewTasks(state.newTasksDisplay);
    state.setUpdatedTasks(state.updatedTasksDisplay);
  }

  discardDisplayState(runtime: CopilotPanelRuntime): void {
    const state = this.getState(runtime);
    if (!state) return;

    state.setNewTasksDisplay(state.newTasks);
    state.setUpdatedTasksDisplay(state.updatedTasks);
  }

  private applyProposal(state: CopilotAuditFindingsPanelState, proposal: TenantProcessStateAuditFindings): void {
    state.setInternalAuditId(proposal.internalAuditId);
    state.setNewTasks(proposal.newTasks);
    state.setNewTasksDisplay(proposal.newTasks);
    state.setUpdatedTasks(proposal.updatedTasks);
    state.setUpdatedTasksDisplay(proposal.updatedTasks);
    state.setOriginals(proposal.originals);
    state.setEligibleTemplateIds(proposal.eligibleTemplateIds);
  }

  private getInternalAuditId(runtime: CopilotPanelRuntime): number | undefined {
    const content = runtime.content;
    if (content instanceof CopilotAuditPreparationContent && content.internalAuditId) {
      return content.internalAuditId;
    }

    //a restored process keeps the audit it was started on
    return this.getState(runtime)?.internalAuditId || undefined;
  }

  private getState(runtime: CopilotPanelRuntime): CopilotAuditFindingsPanelState | undefined {
    return runtime.getUsageState<CopilotAuditFindingsPanelState>(this.context);
  }
}
